import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, LogIn, Sparkles } from "lucide-react";

const DEMO = [
  { label: "Super Admin", email: "admin@maaprint.in", password: "Admin@12345" },
  { label: "Retailer", email: "retailer@maaprint.in", password: "Retailer@123" },
  { label: "Customer", email: "customer@maaprint.in", password: "Customer@123" },
  { label: "Distributor", email: "distributor@maaprint.in", password: "Distributor@123" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const firstName = (u?.name || u?.email || "friend").split(" ")[0];
      toast.success(`Welcome back, ${firstName}!`);
      nav("/dashboard");
    } catch (e) {
      // Differentiate network errors from server errors so users see real reasons.
      let msg;
      if (e?.response?.data?.detail) {
        msg = formatApiErrorDetail(e.response.data.detail);
      } else if (e?.response) {
        msg = `Server error (${e.response.status}). Please try again.`;
      } else if (e?.message) {
        msg = `Network error: ${e.message}`;
      } else {
        msg = "Login failed. Please try again.";
      }
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (d) => {
    setEmail(d.email);
    setPassword(d.password);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left visual */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-brand-blue via-brand-blue to-brand-green overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-20" />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white">
          <Logo size={40} className="[&_span]:!text-white" />
          <div>
            <p className="text-xs uppercase tracking-widest text-white/70 mb-3">
              Smart Cloud Printing Platform
            </p>
            <h2 className="text-4xl font-display font-bold leading-tight max-w-md">
              Silent auto-print for every retailer in India.
            </h2>
            <p className="mt-4 text-white/80 max-w-md">
              Upload PDF from anywhere. Track every print. Auto-manage wallets and commissions.
            </p>
          </div>
          <div className="text-xs text-white/60">© {new Date().getFullYear()} MaaPrint</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Logo size={40} />
          </div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Sign in to MaaPrint</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Or <Link to="/register" className="text-primary font-medium hover:underline" data-testid="login-register-link">create a new account</Link>
          </p>

          <Card className="mt-8 p-6 border-border bg-card">
            <form onSubmit={submit} className="space-y-4">
              {err && (
                <Alert variant="destructive" data-testid="login-error">
                  <AlertDescription>{err}</AlertDescription>
                </Alert>
              )}
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="login-email-input"
                  className="mt-1.5"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-link">
                    Forgot?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                  className="mt-1.5"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-brand-blue-hover text-primary-foreground h-11"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <LogIn size={16} className="mr-2" />}
                Sign in
              </Button>
            </form>
          </Card>

          <Card className="mt-4 p-4 border-border bg-secondary/50">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} className="text-brand-green" />
              <span className="text-xs font-medium uppercase tracking-wider">Try a demo account</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <Button
                  key={d.email}
                  variant="outline"
                  size="sm"
                  className="justify-start text-xs"
                  onClick={() => fillDemo(d)}
                  data-testid={`demo-${d.label.toLowerCase().replace(" ", "-")}-btn`}
                >
                  {d.label}
                </Button>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
